import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import React, { useCallback, useMemo } from 'react';
import { getTermsValue } from 'shared/json_query/reducers/helpers';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { isCianPartner as isCianPartnerUtil } from 'shared/utils/dailyrent/isCianPartner';

import { Card } from '../../../components/Card';
import { useOfferContext } from '../../../context';
import { isCommercialMultiAds } from '../../../utils/computedProps/commercial/multiAds/isCommercialMultiAds';
import { hasGalleryThumbnails } from '../../../utils/computedProps/hasGalleryThumbnails';
import { getPromotionType } from '../../../utils/getPromotionType';
import { shouldShowPromotionDescription } from '../../../utils/shouldShowPromotionDescription';
import { AgentContainer } from '../AgentContainer';
import { ControlsContainer } from '../ControlsContainer';
import { GalleryContainer } from '../GalleryContainer';
import { OfferGeneralInfoContainer } from '../OfferGeneralInfoContainer';
import { PessimizationTogglerContainer } from '../PessimizationTogglerContainer';
import { getMortgageType } from '../PriceContainer/utils';
import { ThumbnailsContainer } from '../ThumbnailsContainer';
import { VASContainer } from '../VAS/VASContainer';

import { trackPromotedOfferShowTelemetry } from './telemetry';

export function OfferCardContainer() {
  const { config, telemetry } = useApplicationContext();

  const isNewHighlighting = config.get<boolean>('new_highlighting') || false;
  const isCpcAuctionEnabled = config.get<boolean>('auction.cpcEnabled') || false;
  const isPerformanceVasEnabled = config.get<boolean>('isPerformanceVASPilot.Enabled') || false;

  const {
    isColored,
    offer,
    jsonQuery,
    api: { isPopupsOpened, onPageJumped },
    kp,
    tracking,
    isNewbuilding,
    thumbnails,
    subtitle,
    isTop,
    user,
  } = useOfferContext();

  const { fullUrl, externalOfferUrl, dailyrent } = offer;
  const showGalleryThumbnails = hasGalleryThumbnails(offer);

  const isCianPartner = isCianPartnerUtil(offer);

  // Получаем выбранные типы ипотеки из фильтров
  const promoSearchTypes = getTermsValue('promo_search_types')(jsonQuery);
  const mortgageType = getMortgageType(promoSearchTypes);

  // Формируем href с учетом типа ипотеки
  const href = useMemo(() => {
    const baseUrl = isCianPartner ? externalOfferUrl : fullUrl;

    if (!mortgageType || isCianPartner) {
      return baseUrl;
    }

    // Проверяем, есть ли уже query параметры в URL
    const hasQueryParams = baseUrl.includes('?');
    const separator = hasQueryParams ? '&' : '?';

    return `${baseUrl}${separator}mortgage_type=${mortgageType}`;
  }, [isCianPartner, externalOfferUrl, fullUrl, mortgageType]);

  const rel = isCianPartner ? 'noreferrer nofollow noindex' : 'noreferrer';

  const handleCardClick = useCallback(() => {
    if (isPopupsOpened()) {
      return;
    }
    if (isCommercialMultiAds(offer)) {
      tracking.trackMultiOfferClick('card_detail_in_newlist');
    }
    onPageJumped('card_detail_in_newlist');
  }, [isPopupsOpened, onPageJumped, offer, tracking]);

  const promotionType = getPromotionType(offer, isCpcAuctionEnabled);

  const onAfterViewedHandle = () => {
    tracking.trackOfferCardShow();

    // Телеметрия показов продвигаемых объявлений для Grafana: считаем признаки, только если объявление
    // продвигается (cpc || cpt) И признак реально отрисован (предикаты из тех же computed-пропсов,
    // что потребляет рендер — thumbnails/subtitle/isTop, а не сырые флаги оффера).
    if (promotionType) {
      trackPromotedOfferShowTelemetry(telemetry, {
        hasPromoPhoto: thumbnails !== null,
        hasPromoTitle: Boolean(isTop) || subtitle !== undefined,
        hasPromoDescription: shouldShowPromotionDescription({
          offer,
          user,
          isCpcAuctionEnabled,
          isPerformanceVasEnabled,
        }),
      });
    }
  };

  return (
    <ActionAfterViewObserver percentVisible={0.5} callback={onAfterViewedHandle} triggerOnce callbackDelay={1000}>
      <Card
        href={href}
        onClick={handleCardClick}
        isWide={showGalleryThumbnails || Boolean(kp?.villageAdvantages) || isNewbuilding}
        colored={!isNewHighlighting && isColored}
        gallery={<GalleryContainer />}
        thumbnails={<ThumbnailsContainer />}
        content={<OfferGeneralInfoContainer isNewHighlighting={isNewHighlighting} />}
        agent={<AgentContainer />}
        controls={<ControlsContainer />}
        showVas={!dailyrent?.hotelData?.isHotel}
        vas={<VASContainer />}
        pessimization={<PessimizationTogglerContainer />}
        comment={null}
        rel={rel}
      />
    </ActionAfterViewObserver>
  );
}
